export const mockData = {
  hero: {
    name: "Gargi Sahu",
    tagline: "Next-Gen Security Engineer | Building Tomorrow's Secure Digital Infrastructure",
    description: "Fresh CS graduate (July 2025) with a rare combination of cybersecurity expertise and full-stack engineering skills. Ready to protect and build the future of digital innovation at scale.",
    social: {
      github: "https://github.com/gargi04sahu",
      linkedin: "https://www.linkedin.com/in/engineer-gargi-sahu/",
      email: "gargi04sahu@gmail.com"
    }
  },
  about: {
    title: "About Me",
    description: "I don't just write code—I architect secure digital experiences. As a fresh CS graduate with hands-on cybersecurity training and full-stack development expertise, I bring a unique perspective that most engineers lack: the ability to build AND protect. My mission is to create technology that's not just innovative, but inherently secure.",
    highlights: [
      "🛡️ Cybersecurity-First Developer: Every line of code written with security in mind",
      "🚀 Full-Stack Engineer: From React frontends to FastAPI backends, I build complete solutions",
      "🤖 AI Security Pioneer: Integrating machine learning with cybersecurity practices",
      "🎯 SOC-Trained Professional: Real-world threat analysis and incident response experience"
    ],
    stats: [
      { label: "Computer Science Graduate", value: "2025" },
      { label: "Key Projects", value: "4" },
      { label: "Security Tools", value: "6+" },
      { label: "Languages Mastered", value: "5+" }
    ]
  },
  skills: {
    title: "Technical Arsenal",
    categories: [
      {
        title: "Security & Defense",
        skills: ["Burp Suite", "Kali Linux", "Nmap", "Metasploitable", "Tenable Nessus", "SOC Operations"]
      },
      {
        title: "Full-Stack Development",
        skills: ["React", "FastAPI", "Python", "JavaScript", "Node.js", "MongoDB"]
      },
      {
        title: "AI & Machine Learning",
        skills: ["Scikit-learn", "NLP", "Streamlit", "Data Analysis", "Predictive Modeling", "TensorFlow"]
      },
      {
        title: "DevOps & Infrastructure",
        skills: ["Git", "GitHub", "Docker", "Linux", "Cloud Computing", "System Administration"]
      },
      {
        title: "Core Technologies",
        skills: ["Python", "JavaScript", "HTML/CSS", "MySQL", "SQLite"]
      }
    ]
  },
  experience: [
    {
      position: "Cybersecurity Associate",
      company: "Secure Root Risk Advisory LLP",
      duration: "August 2025 – Present",
      type: "On-site",
      description: "Gained practical exposure to cybersecurity fundamentals, vulnerability assessment, and security testing.",
      achievements: [
        "Worked with tools like Burp Suite, Kali Linux, Metasploitable, Tenable Nessus and N-Map for penetration testing",
        "Contributed to strengthening AI security and governance within organizational workflows",
        "Completed SOC (Security Operations Center) training and actively monitored security events",
        "Conducted network security analysis and vulnerability assessments"
      ]
    },
    {
      position: "Software Developer Intern",
      company: "Numetry Technologies",
      duration: "January 2025 – June 2025",
      type: "Remote",
      description: "Designed and optimized ReactJS + MySQL applications with focus on performance and maintainability.",
      achievements: [
        "Improved query response times by 15% and enhanced application maintainability",
        "Developed modular UI components for dashboards, reducing feature development time by 20%",
        "Collaborated with cross-functional teams to accelerate release timelines by 10%",
        "Resolved 30+ bugs/issues during the internship period"
      ]
    }
  ],
  projects: [
    {
      title: "SecureRoot Bot",
      subtitle: "AI-Powered Cybersecurity Assistant",
      description: "Engineered an intelligent chatbot that democratizes cybersecurity knowledge. This isn't just another chatbot—it's a security training revolution that achieved 90%+ accuracy in real-time threat guidance.",
      image: "https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg",
      technologies: ["Python", "Streamlit", "NLP", "AI/ML"],
      features: [
        "🎤 Voice-powered security consultations",
        "📚 Multi-session knowledge retention",
        "⚡ Real-time threat intelligence responses",
        "🎯 90%+ accuracy in security recommendations"
      ],
      impact: "Built to scale security awareness across organizations",
      github: "#",
      demo: "#"
    },
    {
      title: "Smart File Organizer",
      subtitle: "Enterprise-Grade Automation Tool",
      description: "Created an intelligent desktop automation system that processes 500+ files in under 10 seconds with 98% accuracy. This tool showcases my ability to solve real-world productivity challenges at scale.",
      image: "https://images.unsplash.com/photo-1653387319597-84bde7e5368e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjb2RpbmclMjBwcm9ncmFtbWluZ3xlbnwwfHx8fDE3NTg0NzI5OTl8MA&ixlib=rb-4.1.0&q=85",
      technologies: ["Python", "Tkinter", "OS", "Shutil"],
      features: [
        "⚡ Lightning-fast processing: 500+ files in 10 seconds",
        "🎯 98% accuracy in file categorization",
        "👀 Smart preview before execution",
        "↩️ One-click undo functionality"
      ],
      impact: "Productivity solution that saves hours of manual work daily",
      github: "https://github.com/gargi04sahu",
      demo: "#"
    },
    {
      title: "Heart Disease Prediction System",
      subtitle: "ML-Powered Healthcare Analytics",
      description: "Developed a predictive healthcare system using advanced machine learning algorithms. Achieved 85% accuracy in heart disease prediction—technology that could save lives through early detection.",
      image: "https://images.unsplash.com/photo-1653387137517-fbc54d488ed8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxjb2RpbmclMjBwcm9ncmFtbWluZ3xlbnwwfHx8fDE3NTg0NzI5OTl8MA&ixlib=rb-4.1.0&q=85",
      technologies: ["Python", "Streamlit", "SQLite", "Scikit-learn"],
      features: [
        "🏥 85% prediction accuracy using Random Forest & SVM",
        "📊 Real-time health risk assessment dashboard",
        "📈 Processed 300+ patient health records",
        "🔬 Advanced feature engineering and data preprocessing"
      ],
      impact: "Healthcare innovation with life-saving potential",
      github: "https://github.com/gargi04sahu",
      demo: "#"
    },
    {
      title: "E-Commerce Admin Platform",
      subtitle: "Scalable Business Management System",
      description: "Architected a comprehensive 8-module e-commerce admin dashboard that streamlines business operations. Built with scalability in mind—ready for enterprise-level deployments.",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxuZXR3b3JrJTIwc2VjdXJpdHl8ZW58MHx8fHwxNzU4NDczMDA0fDA&ixlib=rb-4.1.0&q=85",
      technologies: ["React", "Node.js", "MongoDB", "Express"],
      features: [
        "🏢 8 comprehensive management modules",
        "📈 Real-time business analytics",
        "🔧 Scalable microservices architecture",
        "⚡ Optimized for high-traffic scenarios"
      ],
      impact: "Enterprise-ready solution for business management",
      github: "#",
      demo: "#"
    }
  ],
  contact: {
    title: "Let's Build Something Extraordinary",
    description: "Ready to bring fresh perspective, cutting-edge skills, and relentless drive to your team. Whether it's securing your infrastructure or building the next breakthrough application, I'm prepared to make an immediate impact.",
    email: "gargi04sahu@gmail.com",
    phone: "+91 7905100359",
    linkedin: "https://www.linkedin.com/in/engineer-gargi-sahu/",
    github: "https://github.com/gargi04sahu",
    location: "Kanpur, Uttar Pradesh, India"
  }
};